import type { ApiResponse, Item, Shelf } from '@packages/shared-types';
import { X_TENANT_ID_HEADER } from '@packages/shared-utils';

import { apiClient } from '@/services/apiClient';

export type ShelfCountResult = {
  id: string;
  shelfId: string;
  scannerId: string;
  completedAt: string;
  detectedItemIds: string[];
  missingItemIds?: string[] | null;
};

export type CreateShelfPayload = {
  name: string;
  branchId: string;
  scannerId?: string;
  zone?: string;
  aisle?: string;
  module?: string;
  row?: string;
  cell?: string;
  positionCells?: string[];
};

export type UpdateShelfPayload = {
  name?: string;
  branchId?: string;
  scannerId?: string;
  zone?: string;
  aisle?: string;
  module?: string;
  row?: string;
  cell?: string;
  positionCells?: string[];
};

export type ShelfLocationOptions = {
  zones: string[];
  aisles: string[];
  modules: string[];
  rows: string[];
  cells: string[];
};

export interface ListShelvesPaginatedParams {
  page: number;
  limit: number;
}

export interface ListShelvesPaginatedResult {
  data: Shelf[];
  total: number;
  page: number;
  limit: number;
}

function shelvesHeaders(tenantId?: string | null): Record<string, string> | undefined {
  return tenantId ? { [X_TENANT_ID_HEADER]: tenantId } : undefined;
}

export async function listShelvesPaginated(
  params: ListShelvesPaginatedParams,
  tenantId?: string | null,
): Promise<ListShelvesPaginatedResult> {
  const { page, limit } = params;
  const response = await apiClient.get<ApiResponse<ListShelvesPaginatedResult>>(
    '/api/shelves',
    { params: { page, limit }, headers: shelvesHeaders(tenantId) },
  );
  const payload = response.data.data;
  return {
    data: payload.data,
    total: payload.total,
    page: payload.page,
    limit: payload.limit,
  };
}

export async function listShelves(tenantId?: string | null): Promise<Shelf[]> {
  const response = await apiClient.get<ApiResponse<Shelf[]>>('/api/shelves', {
    headers: shelvesHeaders(tenantId),
  });
  return response.data.data;
}

export async function getShelfById(id: string): Promise<Shelf> {
  const response = await apiClient.get<ApiResponse<Shelf>>(`/api/shelves/${id}`);
  return response.data.data;
}

export async function getShelfLocationOptions(
  branchId?: string,
  tenantId?: string | null,
): Promise<ShelfLocationOptions> {
  const response = await apiClient.get<ApiResponse<ShelfLocationOptions>>(
    '/api/shelves/location-options',
    {
      params: branchId ? { branchId } : undefined,
      headers: shelvesHeaders(tenantId),
    },
  );
  return response.data.data;
}

export async function createShelf(payload: CreateShelfPayload): Promise<Shelf> {
  const response = await apiClient.post<ApiResponse<Shelf>>('/api/shelves', payload);
  return response.data.data;
}

export async function updateShelf(
  id: string,
  payload: UpdateShelfPayload,
): Promise<Shelf> {
  const response = await apiClient.put<ApiResponse<Shelf>>(
    `/api/shelves/${id}`,
    payload,
  );
  return response.data.data;
}

export async function deleteShelf(id: string): Promise<void> {
  await apiClient.delete<ApiResponse<void>>(`/api/shelves/${id}`);
}

export async function getShelfItems(shelfId: string): Promise<Item[]> {
  const response = await apiClient.get<ApiResponse<Item[]>>(
    `/api/shelves/${shelfId}/items`,
  );
  return response.data.data;
}

export async function getShelfCountResult(
  shelfId: string,
): Promise<ShelfCountResult | null> {
  const response = await apiClient.get<ApiResponse<ShelfCountResult | null>>(
    `/api/shelves/${shelfId}/count-result`,
  );
  return response.data.data ?? null;
}
